const VideoElement = globalThis.HTMLElement ?? class {};

export class RilavereConceptVideo extends VideoElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.video = this.querySelector('video');
    this.toggles = Array.from(this.querySelectorAll('[data-video-toggle]'));
    this.film = this.querySelector('[data-video-film]') || this;
    this.document = this.ownerDocument || globalThis.document;
    this.view = this.document?.defaultView || globalThis.window;
    this.motion = this.view?.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.userStarted = false;
    this.userPaused = false;
    this.userOverride = false;
    this.inView = true;
    this.unavailable = !this.video;
    this.playRequest = 0;
    this.connectionToken = {};

    this.handleToggle = (event) => {
      if (this.unavailable) return;

      const starting = !this.userStarted || this.video.paused;
      if (!starting) {
        this.userPaused = true;
        this.cancelPlayback();
        return;
      }

      this.userStarted = true;
      this.userPaused = false;
      this.userOverride = Boolean(this.motion?.matches);

      if (Object.prototype.hasOwnProperty.call(event.currentTarget.dataset, 'videoScroll')) {
        this.film.scrollIntoView?.({
          behavior: this.motion?.matches ? 'instant' : 'smooth',
          block: 'center',
        });
      }

      this.syncPlayback();
    };

    this.handlePlay = () => {
      this.classList.add('is-playing');
      this.updateToggles();
    };
    this.handlePause = () => {
      this.classList.remove('is-playing');
      this.updateToggles();
    };
    this.handleError = () => this.setUnavailable();
    this.syncPlayback = () => {
      if (!this.video || this.unavailable) return;
      const hidden = this.document?.visibilityState === 'hidden';
      const motionBlocked = this.motion?.matches && !this.userOverride;
      const shouldPause = !this.userStarted || this.userPaused || !this.inView || hidden || motionBlocked;

      if (shouldPause) {
        this.cancelPlayback();
        return;
      }

      this.attemptPlay();
    };
    this.handleVisibility = () => this.syncPlayback();
    this.handleMotion = () => {
      if (!this.motion.matches) this.userOverride = false;
      this.syncPlayback();
    };

    for (const toggle of this.toggles) toggle.addEventListener('click', this.handleToggle);

    if (!this.video) {
      this.setUnavailable();
      return;
    }

    this.video.addEventListener('play', this.handlePlay);
    this.video.addEventListener('pause', this.handlePause);
    this.video.addEventListener('error', this.handleError);
    this.document?.addEventListener('visibilitychange', this.handleVisibility);
    if (this.motion?.addEventListener) this.motion.addEventListener('change', this.handleMotion);
    else this.motion?.addListener?.(this.handleMotion);

    const Observer = this.view?.IntersectionObserver || globalThis.IntersectionObserver;
    if (Observer) {
      this.observer = new Observer(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio > 0;
        this.syncPlayback();
      }, { threshold: 0.2 });
      this.observer.observe(this.film);
    }

    this.updateToggles();
  }

  attemptPlay() {
    const request = ++this.playRequest;
    const connectionToken = this.connectionToken;
    const handleRejection = () => {
      if (
        request !== this.playRequest
        || connectionToken !== this.connectionToken
        || this.dataset.bound !== 'true'
      ) return;
      this.userStarted = false;
      this.userPaused = false;
      this.userOverride = false;
      this.classList.remove('is-playing');
      this.updateToggles();
    };
    let playResult;
    try {
      playResult = this.video.play();
    } catch {
      handleRejection();
      return;
    }
    playResult?.catch?.(handleRejection);
  }

  cancelPlayback() {
    this.playRequest += 1;
    if (this.video && !this.video.paused) this.video.pause();
    else this.updateToggles();
  }

  setUnavailable() {
    this.playRequest += 1;
    this.unavailable = true;
    this.classList.remove('is-playing');
    if (this.video && !this.video.paused) this.video.pause();
    this.updateToggles();
  }

  updateToggles() {
    const playing = Boolean(this.video && !this.video.paused && !this.unavailable);
    const label = this.unavailable
      ? (this.dataset.unavailableLabel || 'Film unavailable')
      : !this.userStarted
        ? (this.dataset.watchLabel || 'Play film')
        : playing
          ? (this.dataset.pauseLabel || 'Pause film')
          : (this.dataset.playLabel || 'Play film');

    for (const toggle of this.toggles ?? []) {
      const nestedLabel = toggle.querySelector('[data-video-toggle-label]');
      if (nestedLabel) nestedLabel.textContent = label;
      else toggle.textContent = label;
      toggle.setAttribute('aria-label', label);
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.disabled = this.unavailable;
    }
  }

  disconnectedCallback() {
    this.playRequest += 1;
    this.connectionToken = null;
    this.observer?.disconnect();
    for (const toggle of this.toggles ?? []) toggle.removeEventListener('click', this.handleToggle);
    this.video?.removeEventListener('play', this.handlePlay);
    this.video?.removeEventListener('pause', this.handlePause);
    this.video?.removeEventListener('error', this.handleError);
    this.document?.removeEventListener('visibilitychange', this.handleVisibility);
    if (this.motion?.removeEventListener) this.motion.removeEventListener('change', this.handleMotion);
    else this.motion?.removeListener?.(this.handleMotion);
    if (this.video && !this.video.paused) this.video.pause();
    this.userStarted = false;
    this.userPaused = false;
    this.userOverride = false;
    this.inView = true;
    this.unavailable = !this.video;
    this.classList.remove('is-playing');
    this.updateToggles();
    this.observer = null;
    delete this.dataset.bound;
  }
}

if (globalThis.customElements && !globalThis.customElements.get('rilavere-concept-video')) {
  globalThis.customElements.define('rilavere-concept-video', RilavereConceptVideo);
}
